import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { createPortal } from 'react-dom';
import { useTranslate } from '@bynder/localization';

import { IconCaretDown, IconMenu, IconMoreVertical } from '@bynder/icons';
import { Button, Dropdown } from '@bynder/design-system';
import { InfoContext } from '../../context';
import { NavItem } from '../../types';
import { FadeIn } from '../FadeIn';
import { LiWithFocusHighlight } from '../CommonStyles';
import { BREAKPOINTS } from '../../breakpoints';

/*
 * bynder-platform/bynder/views/navigation/header.cfm
 */
interface Props {
	onReady: () => void;
}

export function NavItems({ onReady }: Props) {
	const [isVisible, setIsVisible] = useState(false);
	const { navItems } = useContext(InfoContext);
	const { translate } = useTranslate();
	const [activeItemId, setActiveItemId] = useState('');

	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [focusedIndex, setFocusedIndex] = useState(-1);
	const dropdownRef = useRef<HTMLUListElement>(null);
	const triggerRef = useRef<HTMLAnchorElement>(null);

	useEffect(() => {
		if (navItems?.length === 0) {
			return;
		}

		setIsVisible(true);
	}, [navItems, translate, isVisible]);

	useEffect(() => {
		if (isVisible) {
			onReady(); // Call on the set state callback
		}
	}, [isVisible, onReady]);

	const toggleDropdown = () => {
		setIsDropdownOpen((prev) => !prev);
		setFocusedIndex(-1);
	};

	const handleKeyDown = (event: React.KeyboardEvent) => {
		const navItemsLength = dropdownRef.current?.children.length ?? 1;

		switch (event.key) {
			case 'ArrowDown':
				event.preventDefault();
				setFocusedIndex((prev) => (prev + 1) % navItemsLength);
				break;
			case 'ArrowUp':
				event.preventDefault();
				setFocusedIndex((prev) => (prev - 1 + navItemsLength) % navItemsLength);
				break;
			case 'Escape':
				event.preventDefault();
				setIsDropdownOpen(false);
				triggerRef.current?.focus();
				break;
			case 'Enter':
				if (!isDropdownOpen) {
					event.preventDefault();
					toggleDropdown();
				} else {
					setIsDropdownOpen(false);
				}

				break;
			default:
				break;
		}
	};

	useEffect(() => {
		if (isDropdownOpen && focusedIndex >= 0 && dropdownRef.current) {
			const item = dropdownRef.current.children[focusedIndex] as HTMLElement;
			item?.focus();
		}
	}, [isDropdownOpen, focusedIndex]);

	const handleClickOutside = (event: MouseEvent) => {
		if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
			setIsDropdownOpen(false);
		}
	};

	useEffect(() => {
		if (isDropdownOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		} else {
			document.removeEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isDropdownOpen]);

	function withoutSharp(linkFragment: string) {
		return linkFragment?.length ? linkFragment.split('#')[0] : '';
	}

	function withoutQueries(linkFragment: string) {
		return linkFragment?.length ? linkFragment.split('?')[0] : '';
	}

	function withoutTrailingSlash(url: string) {
		if (url.length > 0 && url[url.length - 1] === '/') {
			return url.substring(0, url.length - 1);
		}

		return url;
	}

	function withoutStartingSlash(url: string) {
		if (url.length > 0 && url[0] === '/') {
			return url.substring(1, url.length);
		}

		return url;
	}

	const calculateActiveStatus = useCallback(() => {
		const windowLocationPath = window.location.href.substring(window.location.origin.length);
		const windowLocationWithoutHashBang = windowLocationPath.replace(
			new RegExp(/\/#\/|\/#!/g),
			'/',
		);

		// Handle guidelines/home special case
		if (windowLocationWithoutHashBang.includes('/guidelines/home')) {
			const guidelinesHomeItem = navItems.find((item) => item.url === '/');

			if (guidelinesHomeItem) {
				setActiveItemId(guidelinesHomeItem.id);

				return;
			}
		}

		const locationTerms = windowLocationWithoutHashBang
			.split('/')
			.filter((term) => term !== '');

		let activeId = '';
		let previouslyMatchedTerms = 0;

		const perfectMatchNavItem = navItems.find((navItem) => {
			return withoutStartingSlash(windowLocationPath) === withoutStartingSlash(navItem.url);
		});

		if (perfectMatchNavItem) {
			previouslyMatchedTerms = perfectMatchNavItem.url.split('/').filter((fragment) => {
				return fragment?.length;
			}).length;
			activeId = perfectMatchNavItem.id;
		}

		const urlSubPartCoveredCompletely = (windowPath: string, navItemUrl: string) => {
			return (
				windowPath.includes(navItemUrl) ||
				withoutSharp(windowPath).includes(withoutTrailingSlash(withoutQueries(navItemUrl)))
			);
		};

		navItems.forEach((item) => {
			const itemUrlNoHashBang = item.url.replace(new RegExp(/\/#\/|\/#!/g), '/');

			const isDashboard =
				!locationTerms.length ||
				`${locationTerms[0]}/${locationTerms[1]}` === 'account/dashboard';

			if (isDashboard && itemUrlNoHashBang === '/') {
				previouslyMatchedTerms = 2;
				activeId = item.id;
			}

			const matchedSameOrMoreItemsBefore = locationTerms.length <= previouslyMatchedTerms;
			const navItemTerms = itemUrlNoHashBang
				.split('/')
				.filter((term) => withoutSharp(term) !== '');

			const evenFirstItemDoesntMatch = navItemTerms[0] !== withoutSharp(locationTerms[0]);

			if (matchedSameOrMoreItemsBefore || evenFirstItemDoesntMatch) {
				return;
			}

			const matchesCompletely = urlSubPartCoveredCompletely(windowLocationPath, item.url);

			if (!matchesCompletely) {
				return;
			}

			navItemTerms
				.filter((term) => term.length)
				.forEach((itemUrlFragment, index) => {
					if (
						itemUrlFragment === withoutSharp(locationTerms[index]) &&
						previouslyMatchedTerms <= index
					) {
						previouslyMatchedTerms = index + 1;
						activeId = item.id;
					}
				});
		});

		setActiveItemId(activeId);
	}, [navItems]);

	useEffect(() => {
		calculateActiveStatus();
	}, [navItems, calculateActiveStatus]);

	const mobileNav = document.querySelector('#mobile-nav');

	return isVisible ? (
		<FadeUp className="nav-bar-right clearfix">
			<nav className="clearfix">
				<ul className="nav-main clearfix">
					{navItems.map((item) => {
						const isActive = activeItemId === item.id;

						return (
							<Item
								item={item}
								isActive={isActive}
								key={item.id}
								setIsDropdownOpen={setIsDropdownOpen}
								handleKeyDown={handleKeyDown}
							/>
						);
					})}

					<LiWithFocusHighlight className="hidden-nav is-visible">
						<a
							href="#"
							title={translate('generalMore')}
							aria-haspopup
							aria-expanded={isDropdownOpen}
							onKeyDown={handleKeyDown}
							ref={triggerRef}
						>
							<MenuMoreWrapper>
								<IconMoreVertical size="20px" /> {translate('generalMore')}...{' '}
								<IconCaretDown size="20px" />
							</MenuMoreWrapper>

							<HamburgerMenu>
								<IconMenu size="20px" />
							</HamburgerMenu>
						</a>
						<ul
							style={isDropdownOpen ? { display: 'block' } : undefined}
							role="menu"
							className={`dropdown-menu ${isDropdownOpen ? 'is-openn' : ''}`}
							ref={dropdownRef}
							onKeyDown={handleKeyDown}
						/>
					</LiWithFocusHighlight>
				</ul>
			</nav>
			{mobileNav &&
				createPortal(
					<Dropdown
						trigger={({ isOpen, ...triggerProps }) => (
							<Button
								variant="clean"
								aria-label={translate('generalMore')}
								isPressed={isOpen}
								icon={<IconMenu />}
								{...triggerProps}
							/>
						)}
					>
						{navItems.map((item) => (
							<Dropdown.Item href={item.url} key={item.id}>
								{item.label}
							</Dropdown.Item>
						))}
					</Dropdown>,
					mobileNav,
				)}
		</FadeUp>
	) : null;
}

interface ItemProps {
	item: NavItem;
	isActive: boolean;
	setIsDropdownOpen: (value: ((prevState: boolean) => boolean) | boolean) => void;
	handleKeyDown?: (event: React.KeyboardEvent) => void;
}

function Item({ item, isActive, setIsDropdownOpen, handleKeyDown }: ItemProps) {
	const isAbsolute = /^https?:\/\//i.test(item.url);

	return (
		<LiWithFocusHighlight role="menuitem" tabIndex={-1} onKeyDown={handleKeyDown}>
			<a
				href={item.url}
				title={item.label}
				target={isAbsolute ? '_blank' : ''}
				className={isActive ? 'active' : ''}
				rel="noreferrer"
				onClick={() => setIsDropdownOpen(false)}
			>
				{item.label}
			</a>
		</LiWithFocusHighlight>
	);
}

const FadeUp = (props: { children: React.ReactNode; className: string }) => (
	<FadeIn className={props.className}>{props.children}</FadeIn>
);

const MenuMoreWrapper = styled.div`
	@media (max-width: ${BREAKPOINTS.MOBILE}px) {
		display: none;
	}
`;

const HamburgerMenu = styled.div`
	display: none;

	@media (max-width: ${BREAKPOINTS.MOBILE}px) {
		display: block;
	}
`;
